import { Controller } from "@hotwired/stimulus"

// Controller for managing inline todo actions on person profile
export default class extends Controller {
  static targets = ["todoItem", "modalContainer", "modalContent"]
  
  // Toggle todo completion status
  async toggleComplete(event) {
    event.preventDefault()
    const button = event.currentTarget
    const todoItem = button.closest("[data-todo-id]")
    const todoId = todoItem.dataset.todoId
    const isCompleted = todoItem.dataset.completed === "true"
    
    const url = isCompleted ? `/todos/${todoId}/reopen` : `/todos/${todoId}/complete`
    
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateTodoItem(todoItem, data.todo)
      }
    } catch (error) {
      console.error("Error toggling todo:", error)
    }
  }
  
  // Open edit modal
  async openEditModal(event) {
    event.preventDefault()
    const url = event.currentTarget.href || event.currentTarget.dataset.url
    
    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        this.modalContentTarget.innerHTML = html
        this.modalContainerTarget.classList.remove("hidden")
        document.body.classList.add("overflow-hidden")
        
        // Add form submit handler
        const form = this.modalContentTarget.querySelector("form")
        if (form) {
          form.addEventListener("submit", this.handleFormSubmit.bind(this))
        }
      }
    } catch (error) {
      console.error("Error opening modal:", error)
    }
  }
  
  // Handle form submission
  async handleFormSubmit(event) {
    event.preventDefault()
    const form = event.target
    const formData = new FormData(form)
    
    try {
      const response = await fetch(form.action, {
        method: form.method || "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        // Reload the page to show updated data
        window.location.reload()
      } else {
        const html = await response.text()
        this.modalContentTarget.innerHTML = html
        const newForm = this.modalContentTarget.querySelector("form")
        if (newForm) {
          newForm.addEventListener("submit", this.handleFormSubmit.bind(this))
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }
  
  // Close modal
  closeModal(event) {
    if (event) event.preventDefault()
    this.modalContainerTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.modalContentTarget.innerHTML = ""
  }
  
  // Close modal on background click
  closeModalBackground(event) {
    if (event.target === this.modalContainerTarget) {
      this.closeModal()
    }
  }
  
  // Delete todo with confirmation
  async deleteTodo(event) {
    event.preventDefault()
    
    if (!confirm("Are you sure you want to delete this todo?")) {
      return
    }
    
    const button = event.currentTarget
    const todoItem = button.closest("[data-todo-id]")
    const todoId = todoItem.dataset.todoId
    
    try {
      const response = await fetch(`/todos/${todoId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        // Animate removal
        todoItem.style.transition = "opacity 0.3s, transform 0.3s"
        todoItem.style.opacity = "0"
        todoItem.style.transform = "translateX(-10px)"
        
        setTimeout(() => {
          todoItem.remove()
          this.updateEmptyState()
        }, 300)
      }
    } catch (error) {
      console.error("Error deleting todo:", error)
    }
  }
  
  // Archive todo
  async archiveTodo(event) {
    event.preventDefault()
    const button = event.currentTarget
    const todoItem = button.closest("[data-todo-id]")
    const todoId = todoItem.dataset.todoId
    
    try {
      const response = await fetch(`/todos/${todoId}/archive`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })
      
      if (response.ok) {
        // Animate removal
        todoItem.style.transition = "opacity 0.3s, transform 0.3s"
        todoItem.style.opacity = "0"
        todoItem.style.transform = "translateX(-10px)"
        
        setTimeout(() => {
          todoItem.remove()
          this.updateEmptyState()
        }, 300)
      }
    } catch (error) {
      console.error("Error archiving todo:", error)
    }
  }
  
  // Update todo item UI after toggle
  updateTodoItem(todoItem, todo) {
    const isCompleted = todo.status === "completed"
    todoItem.dataset.completed = isCompleted.toString()
    
    // Update checkbox appearance
    const checkbox = todoItem.querySelector("[data-checkbox]")
    if (checkbox) {
      if (isCompleted) {
        checkbox.innerHTML = `
          <div class="w-4 h-4 rounded border-2 border-green-500 bg-green-500 flex items-center justify-center cursor-pointer">
            <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        `
      } else {
        checkbox.innerHTML = `
          <div class="w-4 h-4 rounded border-2 border-slate-300 cursor-pointer hover:border-blue-500"></div>
        `
      }
    }
    
    // Update title styling
    const title = todoItem.querySelector("[data-title]")
    if (title) {
      if (isCompleted) {
        title.classList.add("text-slate-400", "line-through")
        title.classList.remove("text-slate-900")
      } else {
        title.classList.remove("text-slate-400", "line-through")
        title.classList.add("text-slate-900")
      }
    }
    
    // Update action buttons
    const actionsContainer = todoItem.querySelector("[data-actions]")
    if (actionsContainer) {
      // Show/hide complete button
      const completeBtn = actionsContainer.querySelector("[data-action='person-todos#toggleComplete']")
      if (completeBtn) {
        if (isCompleted) {
          // Change to uncomplete icon
          completeBtn.innerHTML = `
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          `
          completeBtn.title = "Mark as pending"
        } else {
          completeBtn.innerHTML = `
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          `
          completeBtn.title = "Complete"
        }
      }
      
      // Show/hide archive button
      let archiveBtn = actionsContainer.querySelector("[data-action='person-todos#archiveTodo']")
      if (isCompleted && !archiveBtn) {
        // Add archive button
        const archiveHtml = `
          <button type="button" 
                  data-action="person-todos#archiveTodo" 
                  class="p-1 text-slate-400 hover:text-amber-600 rounded" 
                  title="Archive">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>
        `
        const deleteBtn = actionsContainer.querySelector("[data-action='person-todos#deleteTodo']")
        if (deleteBtn) {
          deleteBtn.insertAdjacentHTML("beforebegin", archiveHtml)
        }
      } else if (!isCompleted && archiveBtn) {
        archiveBtn.remove()
      }
    }
  }
  
  // Check if todo list is empty and show empty state
  updateEmptyState() {
    const todoList = this.element.querySelector("[data-todo-list]")
    const emptyState = this.element.querySelector("[data-empty-state]")
    const todoItems = this.element.querySelectorAll("[data-todo-id]")
    
    if (todoItems.length === 0) {
      if (todoList) todoList.classList.add("hidden")
      if (emptyState) emptyState.classList.remove("hidden")
    }
  }
  
  // Handle escape key to close modal
  handleKeydown(event) {
    if (event.key === "Escape" && !this.modalContainerTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }
  
  connect() {
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }
  
  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }
  
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}

